import React, { useState, useEffect, useContext } from "react";
import { Modal, Button, Form, Row, Col, Alert, Spinner } from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { TranslatorContext } from "../../../../context/Translator";

export default function ProductEditModal({ show, handleClose, product, onUpdateSuccess, filterData }) {
    const { t } = useContext(TranslatorContext);
    const [formData, setFormData] = useState({});
    const [files, setFiles] = useState({ first_img: null, card_img: null });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (product) {
            setFormData({
                title: product.title || "",
                category_id: product.category_id || product.category?.id || "",
                brand_id: product.brand_id || product.brand?.id || "",
                store_id: product.store_id || product.store?.id || "",
                description: product.description || "",
                short_description: product.short_description || "",
                product_condition: product.product_condition || "",
                selling_style: product.selling_style || "",
                buying_price: product.pricing?.buying_price || product.buying_price || "",
                short_buying_price: product.pricing?.short_buying_price || product.short_buying_price || "",
                selling_price_dollar: product.pricing?.selling_price_dollar || product.selling_price_dollar || "",
                sell_price_dollar: product.pricing?.sell_price_dollar || product.sell_price_dollar || "",
                shipping_charges_dollar: product.pricing?.shipping_charges_dollar || product.shipping_charges_dollar || "",
                qty: product.qty || ""
            });
            setFiles({ first_img: null, card_img: null });
            setError(null);
        }
    }, [product]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleDescriptionChange = (content) => {
        setFormData((prev) => ({ ...prev, description: content }));
    };

    const handleFileChange = (e) => {
        const { name, files: selectedFiles } = e.target;
        if (selectedFiles && selectedFiles[0]) {
            setFiles((prev) => ({ ...prev, [name]: selectedFiles[0] }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            const token = localStorage.getItem("authToken");
            const data = new FormData();

            Object.keys(formData).forEach((key) => {
                if (formData[key] !== "" && formData[key] !== null && formData[key] !== undefined) {
                    data.append(key, formData[key]);
                }
            });

            if (files.first_img) data.append("first_img", files.first_img);
            if (files.card_img) data.append("card_img", files.card_img);

            data.append("_method", "PUT");

            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/admin/products/${product.id}`,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data"
                    }
                }
            );

            if (res.status === 200 || res.data?.success) {
                onUpdateSuccess(res.data?.data || res.data);
                handleClose();
            }
        } catch (err) {
            console.error("Error updating product:", err);
            setError(err.response?.data?.message || "Failed to update product.");
        } finally {
            setSubmitting(false);
        }
    };

    const quillModules = {
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["clean"]
        ]
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered className="custom-edit-modal">
            <Modal.Header closeButton className="px-4 py-3 border-bottom">
                <Modal.Title className="fw-bold fs-5 text-dark">{t("Edit Product")}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body className="px-4 py-3" style={{ maxHeight: "75vh", overflowY: "auto" }}>
                    {error && <Alert variant="danger" className="py-2">{error}</Alert>}

                    <Row className="g-3">
                        <Col md={12}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Title")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="title"
                                    value={formData.title || ""}
                                    onChange={handleChange}
                                    required
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Brand")}</Form.Label>
                                <Form.Select name="brand_id" value={formData.brand_id || ""} onChange={handleChange} className="custom-input">
                                    <option value="">{t("Select Brand")}</option>
                                    {filterData.brands?.map((b) => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Store")}</Form.Label>
                                <Form.Select name="store_id" value={formData.store_id || ""} onChange={handleChange} className="custom-input">
                                    <option value="">{t("Select Store")}</option>
                                    {filterData.stores?.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Quantity")}</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="qty"
                                    value={formData.qty || ""}
                                    onChange={handleChange}
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Buying Price")}</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    name="buying_price"
                                    value={formData.buying_price || ""}
                                    onChange={handleChange}
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Selling Price")}</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    name="selling_price_dollar"
                                    value={formData.selling_price_dollar || ""}
                                    onChange={handleChange}
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Sell Price ($)")}</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    name="sell_price_dollar"
                                    value={formData.sell_price_dollar || ""}
                                    onChange={handleChange}
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Short Buying Price")}</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    name="short_buying_price"
                                    value={formData.short_buying_price || ""}
                                    onChange={handleChange}
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Shipping Charges ($)")}</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    name="shipping_charges_dollar"
                                    value={formData.shipping_charges_dollar || ""}
                                    onChange={handleChange}
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Product Condition")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="product_condition"
                                    value={formData.product_condition || ""}
                                    onChange={handleChange}
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Selling Style")}</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="selling_style"
                                    value={formData.selling_style || ""}
                                    onChange={handleChange}
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={12}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Short Description")}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    name="short_description"
                                    value={formData.short_description || ""}
                                    onChange={handleChange}
                                    className="custom-input"
                                />
                            </Form.Group>
                        </Col>

                        <Col md={12}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Description")}</Form.Label>
                                <div className="quill-wrapper">
                                    <ReactQuill
                                        theme="snow"
                                        value={formData.description || ""}
                                        onChange={handleDescriptionChange}
                                        modules={quillModules}
                                    />
                                </div>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("First Image")}</Form.Label>
                                <Form.Control type="file" name="first_img" onChange={handleFileChange} accept="image/*" className="custom-input" />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-muted small text-uppercase mb-1">{t("Card Image")}</Form.Label>
                                <Form.Control type="file" name="card_img" onChange={handleFileChange} accept="image/*" className="custom-input" />
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>

                <Modal.Footer className="px-4 py-3 border-top bg-light">
                    <Button variant="secondary" onClick={handleClose} disabled={submitting} className="px-4">
                        {t("CANCEL")}
                    </Button>
                    <Button variant="primary" type="submit" disabled={submitting} className="px-4">
                        {submitting ? <Spinner animation="border" size="sm" /> : t("SAVE CHANGES")}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}